import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Sparkles, Check, X, Edit3, Send } from 'lucide-react';
import { Separator } from '@/components/ui/separator';

interface AISuggestionData {
  id: string;
  content: string;
  confidence: number;
  aiSource: {
    modelUsed: string;
    provider: string;
    generatedAt: string;
  };
}

interface AISuggestionBarProps {
  suggestion: AISuggestionData;
  onAccept: () => void;
  onEdit: (editedContent: string) => void;
  onDiscard: () => void;
  disabled?: boolean;
  aiMode?: 'auto' | 'suggestions' | 'manual';
}

export function AISuggestionBar({
  suggestion,
  onAccept,
  onEdit,
  onDiscard,
  disabled = false,
  aiMode = 'manual'
}: AISuggestionBarProps) {
  const { t } = useTranslation('conversations');
  const [isEditing, setIsEditing] = useState(false);
  const [editedContent, setEditedContent] = useState(suggestion.content);

  const handleStartEdit = () => {
    setIsEditing(true);
    setEditedContent(suggestion.content);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditedContent(suggestion.content);
  };

  const handleSendEdit = () => {
    onEdit(editedContent);
    setIsEditing(false);
  };

  return (
    <Card className="border-primary/20 bg-primary/5">
      <CardContent className="p-3">
        <div className="space-y-3">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-primary" />
              <span className="text-sm font-medium text-primary">
                {isEditing ? t('aiSuggestion.editTitle') : t('aiSuggestion.title')}
              </span>
              {!isEditing && (
                <Badge variant="secondary" className="text-xs">
                  {Math.round(suggestion.confidence * 100)}% {t('aiSuggestion.confidence')}
                </Badge>
              )}
            </div>
            
            {aiMode !== 'suggestions' && (
              <div className="flex items-center gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onDiscard}
                  disabled={disabled}
                  className="h-7 w-7 p-0 hover:bg-destructive/20 hover:text-destructive"
                >
                  <X className="h-3 w-3" />
                </Button>
              </div>
            )}
          </div>

          {/* AI Suggestion Content */}
          <div className="bg-background rounded-md p-3 border">
            {isEditing ? (
              <Textarea
                value={editedContent}
                onChange={(e) => setEditedContent(e.target.value)}
                placeholder={t('aiSuggestion.editPlaceholder')}
                className="min-h-[80px] resize-none border-0 p-0 focus-visible:ring-0 focus-visible:ring-offset-0"
                disabled={disabled}
              />
            ) : (
              <p className="text-sm whitespace-pre-wrap">{suggestion.content}</p>
            )}
          </div>

          {/* Metadata */}
          {!isEditing && (
            <>
              <div className="flex items-center justify-between text-xs text-muted-foreground">
                <span>{t('aiSuggestion.generatedBy')} {suggestion.aiSource.modelUsed}</span>
                <span>{new Date(suggestion.aiSource.generatedAt).toLocaleTimeString()}</span>
              </div>
              <Separator />
            </>
          )}

          {/* Action Buttons */}
          <div className="flex items-center gap-2">
            {isEditing ? (
              <>
                <Button
                  size="sm"
                  onClick={handleSendEdit}
                  disabled={disabled || !editedContent.trim()}
                  className="flex-1 gap-2"
                >
                  <Send className="h-3 w-3" />
                  {t('aiSuggestion.send')}
                </Button>

                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleCancelEdit}
                  disabled={disabled}
                  className="flex-1 gap-2"
                >
                  <X className="h-3 w-3" />
                  {t('aiSuggestion.cancel')}
                </Button>
              </>
            ) : (
              <>
                <Button
                  size="sm"
                  onClick={onAccept}
                  disabled={disabled}
                  className="flex-1 gap-2"
                >
                  <Check className="h-3 w-3" />
                  {t('aiSuggestion.sendAsIs')}
                </Button>

                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleStartEdit}
                  disabled={disabled}
                  className="flex-1 gap-2"
                >
                  <Edit3 className="h-3 w-3" />
                  {t('aiSuggestion.editAndSend')}
                </Button>
              </>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}